import { getSignatureUserId } from "./onboardingStatus";

/**
 * Owner's signing rule: a document generated FROM A TEMPLATE (a DB template
 * or a system-built one, e.g. order amendments / leave requests) may never
 * have a signature / signature request removed — only the whole document can
 * be withdrawn (see files/withdraw.js on the backend). An uploaded document
 * with no template keeps the old per-signer cancel behaviour.
 *
 * Single source of truth for that predicate — mirrors the backend's exact
 * check, use this everywhere instead of re-deriving it ad hoc.
 */
export function isTemplateGeneratedFile(
  file:
    | { templateId?: { id?: string } | string | null; systemTemplateKey?: string | null }
    | null
    | undefined
): boolean {
  if (!file) return false;
  const templateId =
    file.templateId && typeof file.templateId === "object"
      ? file.templateId.id
      : file.templateId;
  return !!templateId || !!file.systemTemplateKey;
}

/**
 * Whether signature/signature-request removal is locked on this file —
 * i.e. whether a signer may only be added, never removed one by one (the
 * whole document must be withdrawn instead). Backend contract v2: the file
 * payload carries a computed `signaturesLocked` boolean and this is
 * authoritative when present — some template-generated documents (e.g.
 * work-security role documents) are deliberately NOT locked and keep the
 * old removal behaviour. `isTemplateGeneratedFile` is only a client-side
 * fallback for payloads that don't carry the field yet.
 *
 * Single source of truth — use this everywhere a "can this signer be
 * removed / can this signer list be edited freely" check is needed, instead
 * of calling isTemplateGeneratedFile directly.
 */
export function isSignatureLockedFile(
  file:
    | {
        signaturesLocked?: boolean;
        templateId?: { id?: string } | string | null;
        systemTemplateKey?: string | null;
      }
    | null
    | undefined
): boolean {
  if (!file) return false;
  if (typeof file.signaturesLocked === "boolean") return file.signaturesLocked;
  return isTemplateGeneratedFile(file);
}

/**
 * `withdrawnBy` (like `annulledBy`) is stored as a bare user id — the file
 * payload usually already carries a handful of named users though (uploader,
 * assigned user, signers, reviewers). If the id matches one of those, return
 * its name; otherwise the caller should just omit the name rather than show
 * a bare id.
 */
export function resolveKnownUserName(
  file: any,
  userId: string | null | undefined
): string | null {
  if (!file || !userId) return null;
  const candidates: any[] = [
    file.user,
    file.assignedUser,
    ...(Array.isArray(file.signersList)
      ? file.signersList.map((signer: any) => signer?.user)
      : []),
    ...(Array.isArray(file.dokobit?.signersList) ? file.dokobit.signersList : []),
    ...(Array.isArray(file.reviewByUsers)
      ? file.reviewByUsers.map((entity: any) => entity?.user)
      : []),
  ];
  const match = candidates.find(
    (candidate) => candidate && String(candidate.id) === String(userId)
  );
  return match?.name || null;
}

/**
 * Whether the "Withdraw document" action should be offered: only for a
 * template-generated document that is not yet fully signed and not already
 * annulled/withdrawn, and only to the file's own uploader or a company
 * Master. Single source of truth for this predicate — file-view-container,
 * authorized-file-view-container and customer-finance all show the same
 * action under the same rule, and should not re-derive it ad hoc.
 *
 * Backend contract v2: withdrawal is only supported for custom-template
 * documents in a signing flow — NOT system-template module documents
 * (onboarding, contract amendments, business trips, fuel reports, invoices)
 * and NOT customer-finance documents, even though those can be
 * template-generated too. The file payload carries a computed
 * `withdrawSupported` boolean for this; require it explicitly true (absent
 * — an older payload, or a file shape that never supports it — hides the
 * action, it does not fall back to guessing).
 */
export function canWithdrawDocument(
  file:
    | {
        templateId?: { id?: string } | string | null;
        systemTemplateKey?: string | null;
        isSigned?: boolean;
        withdrawnAt?: number | null;
        annulledAt?: number | null;
        user?: { id?: string } | null;
        withdrawSupported?: boolean;
      }
    | null
    | undefined,
  {
    currentUserId,
    isMaster,
  }: { currentUserId?: string | null; isMaster?: boolean }
): boolean {
  if (!file) return false;
  const isOwner = !!currentUserId && file.user?.id === currentUserId;
  return (
    isTemplateGeneratedFile(file) &&
    file.withdrawSupported === true &&
    !file.isSigned &&
    !file.withdrawnAt &&
    !file.annulledAt &&
    (isOwner || !!isMaster)
  );
}

export const getSignatureStatusPriority = (status: string) => {
  if (status === "declined") return 3;
  if (status === "pending") return 2;
  if (status === "signed") return 1;
  return 0;
};

export const dedupeSignaturesByUser = (
  signatures: any[],
  options: {
    includeResendSignatureId?: boolean;
    includeLatestSigningTime?: boolean;
  } = {}
) => {
  const { includeResendSignatureId = false, includeLatestSigningTime = false } =
    options;

  return Object.values(
    signatures.reduce((acc: Record<string, any>, sig: any) => {
      const uid = getSignatureUserId(sig);
      const key = uid || (sig.id ? `sig:${sig.id}` : "");
      if (!key) return acc;

      if (!acc[key]) {
        acc[key] = {
          ...sig,
          ...(includeResendSignatureId
            ? { resendSignatureId: sig.status === "pending" ? sig.id : null }
            : {}),
        };
        return acc;
      }

      const existing = acc[key];
      if (
        getSignatureStatusPriority(sig.status) >
        getSignatureStatusPriority(existing.status)
      ) {
        existing.status = sig.status;
      }
      if (
        includeResendSignatureId &&
        sig.status === "pending" &&
        !existing.resendSignatureId
      ) {
        existing.resendSignatureId = sig.id;
      }
      if (!existing.userName && sig.userName) {
        existing.userName = sig.userName;
      }
      if (includeLatestSigningTime && sig.signingTime) {
        if (
          !existing.signingTime ||
          new Date(sig.signingTime).getTime() >
            new Date(existing.signingTime).getTime()
        ) {
          existing.signingTime = sig.signingTime;
        }
      }

      return acc;
    }, {})
  );
};

/**
 * Signer entity shape from signersList (ref, user, position?).
 * user may have isDisabled when enriched by backend.
 */
interface SignerEntity {
  ref: string;
  user: any;
  position?: string;
}

interface ExtractedSigners {
  /** First contact signer (for backward compat / single-contact UI). */
  contactSigner: any;
  /** All contact signers from signersList (supports multiple contacts). */
  contactSigners: any[];
  permissionUserSigner: any;
  userSigner: any;
  /** User with ref "Users" and no position (non-tech user). Used by BuildPdfWithSigners for selectedNonTechUser. */
  nonTechUserSigner: any;
}

/**
 * Extract contactSigner(s), permissionUserSigner, userSigner, and nonTechUserSigner from signersList,
 * skipping disabled company users (ref === "Users" && user.isDisabled === true).
 * Used when opening signature modals so disabled users are not pre-filled.
 *
 * - contactSigner: First contact (single); contactSigners: all contacts (array)
 * - permissionUserSigner: User with position "permissionuser"
 * - userSigner: Any User that is not permissionuser (for customer-finance, features/files, order-view-container)
 * - nonTechUserSigner: User with ref "Users" and no position (for BuildPdfWithSigners selectedNonTechUser)
 *
 * @param signersList - Array of { ref, user, position? }
 * @returns { contactSigner, contactSigners, permissionUserSigner, userSigner, nonTechUserSigner }
 */
/**
 * Safely extract the ID from a signer value that may be a string ID or a populated object.
 */
export function getSignerId(signer: any): string | null {
  if (!signer) return null;
  if (typeof signer === 'string') return signer;
  return signer?.id || null;
}

export function extractSignersFromList(
  signersList: SignerEntity[] | undefined | null
): ExtractedSigners {
  let contactSigners: any[] = [];
  let permissionUserSigner: any = null;
  let userSigner: any = null;
  let nonTechUserSigner: any = null;

  if (!signersList || !Array.isArray(signersList)) {
    return {
      contactSigner: null,
      contactSigners: [],
      permissionUserSigner,
      userSigner,
      nonTechUserSigner,
    };
  }

  signersList.forEach((signerEntity) => {
    if (signerEntity.ref === "Contacts" && signerEntity.user != null) {
      contactSigners.push(signerEntity.user);
    }
    if (
      signerEntity.ref === "Users" &&
      (signerEntity.user?.isDisabled === true)
    ) {
      return;
    }
    if (
      signerEntity.ref === "Users" &&
      signerEntity.position === "permissionuser"
    ) {
      permissionUserSigner = signerEntity.user;
    } else if (signerEntity.ref === "Users") {
      userSigner = signerEntity.user;
      if (!signerEntity.position) {
        nonTechUserSigner = signerEntity.user;
      }
    }
  });

  const contactSigner = contactSigners.length > 0 ? contactSigners[0] : null;
  return {
    contactSigner,
    contactSigners,
    permissionUserSigner,
    userSigner,
    nonTechUserSigner,
  };
}

/**
 * Get non-disabled company user IDs from a signers list for the multi-user assign modal.
 * Accepts either:
 * - Signer entities: { ref, user?, position? } – only "Users" ref, excludes disabled
 * - Plain user objects: { id, isDisabled? } – excludes disabled
 * Contacts are not included (modal expects company user IDs only).
 *
 * @param userSigners - Array of signer entities or user objects
 * @returns Array of user ID strings
 */
export function getNonDisabledUserIds(
  userSigners: any[] | undefined | null
): string[] {
  if (!userSigners || !Array.isArray(userSigners)) {
    return [];
  }
  const ids: string[] = [];
  userSigners.forEach((entity) => {
    if (entity?.user != null) {
      // Signer entity: { ref, user, position? }
      if (
        entity.ref === "Users" &&
        entity.user?.isDisabled !== true &&
        (entity.user?.id ?? entity.id)
      ) {
        ids.push(String(entity.user?.id ?? entity.id));
      }
    } else if (entity?.id != null && entity?.isDisabled !== true) {
      // Plain user object: { id, isDisabled? }
      ids.push(String(entity.id));
    }
  });
  return ids.filter((id) => id !== "");
}
